import { ArrowRight, Zap, MapPin, Sparkles } from "lucide-react";
import { Rich } from "@/components/Rich";
import { withDefaults, resolveImg } from "@/lib/sections";

const STAT_COLORS = ["text-sky-400", "text-orange-500", "text-white"];

export default function Hero({ content }) {
  const c = withDefaults("hero", content);
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="inicio"
      data-testid="section-hero"
      className="relative pt-28 sm:pt-36 pb-20 sm:pb-28 overflow-hidden"
    >
      {/* Background grid + gradients */}
      <div className="absolute inset-0 tech-grid opacity-40" aria-hidden />
      <div
        className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-sky-500/10 blur-3xl"
        aria-hidden
      />
      <div
        className="absolute top-20 -right-32 w-[480px] h-[480px] rounded-full bg-orange-500/10 blur-3xl"
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7 section-fade-in">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sky-500/20 bg-sky-500/5 text-sky-400 font-mono text-xs"
              data-testid="hero-badge"
            >
              <Sparkles size={14} />
              <span>{c.badge}</span>
            </div>

            <h1
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]"
              data-testid="hero-title"
            >
              <Rich text={c.title} />
            </h1>

            <p
              className="mt-6 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
              data-testid="hero-subtitle"
            >
              {c.subtitle}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                onClick={() => goTo("cotizador")}
                data-testid="hero-cta-quote"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-white bg-orange-500 hover:bg-orange-600 transition-all shadow-[0_0_25px_rgba(249,115,22,0.35)] hover:shadow-[0_0_35px_rgba(249,115,22,0.55)]"
              >
                {c.cta_primary}
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
              <button
                onClick={() => goTo("agendar")}
                data-testid="hero-cta-book"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg font-semibold text-sky-400 hover:text-sky-300 bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-sky-500/30 transition-all"
              >
                {c.cta_secondary}
              </button>
            </div>

            {/* Stats strip */}
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
              {(c.stats || []).map((st, i) => (
                <div key={i} data-testid={`hero-stat-${i + 1}`}>
                  <div className={`font-mono text-2xl font-bold ${STAT_COLORS[i % 3]}`}>{st.value}</div>
                  <div className="text-xs text-slate-500 mt-1">{st.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: code-editor mockup */}
          <div className="lg:col-span-5 section-fade-in">
            {c.image ? (
              <img src={resolveImg(c.image)} alt="" data-testid="hero-image" className="w-full rounded-xl border border-slate-800 object-cover shadow-2xl" />
            ) : (
            <div
              data-testid="hero-code-card"
              className="relative rounded-xl border border-slate-800 bg-slate-900/80 backdrop-blur-sm shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500/80" />
                  <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <span className="w-3 h-3 rounded-full bg-green-500/80" />
                </div>
                <span className="font-mono text-xs text-slate-500">
                  divelopers ~ tu-negocio.com
                </span>
              </div>
              <pre className="px-5 py-5 text-[13px] leading-7 font-mono text-slate-300 overflow-x-auto">
{`> npx divelopers init
✔ Detectando ubicación  
✔ Optimizando SEO local
✔ Conectando WhatsApp
✔ Indexando Google Maps

`}<span className="text-orange-500">// listo en </span><span className="text-sky-400">72h</span>{`
`}<span className="text-orange-500">$ </span><span className="text-white">deploy --production</span>{`
`}<span className="text-emerald-400">✓ Live: tu-negocio.com</span>
              </pre>
              <div className="px-5 py-4 border-t border-slate-800 grid grid-cols-3 gap-3 text-xs">
                <div className="flex items-center gap-2 text-slate-400">
                  <Zap size={14} className="text-orange-500" /> Veloz
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <MapPin size={14} className="text-sky-400" /> Local
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Sparkles size={14} className="text-orange-500" /> IA
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
